import {
  Component,
  OnInit,
  ViewChild,
  ElementRef,
  inject,
} from '@angular/core';
import {
  UntypedFormGroup,
  FormGroup,
  FormControl,
  Validators,
  ReactiveFormsModule,
} from '@angular/forms';
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogRef,
  MatDialogModule,
} from '@angular/material/dialog';
import { MatChipInputEvent, MatChipsModule } from '@angular/material/chips';
import { COMMA, ENTER } from '@angular/cdk/keycodes';
import {
  MatAutocompleteSelectedEvent,
  MatAutocompleteModule,
} from '@angular/material/autocomplete';
import { MatSnackBar } from '@angular/material/snack-bar';
import { WarnDialogComponent } from '../../../warn-dialog/warn-dialog.component';
import { Observable } from 'rxjs';
import { startWith, map } from 'rxjs/operators';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { AsyncPipe } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatInputModule } from '@angular/material/input';

@Component({
  selector: 'app-admin-role-dialog',
  templateUrl: './role-dialog.component.html',
  styleUrls: ['./role-dialog.component.scss'],
  imports: [
    MatDialogModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatChipsModule,
    MatAutocompleteModule,
    MatIconModule,
    AsyncPipe,
    MatButtonModule,
    MatInputModule,
  ],
})
export class RoleDialogComponent implements OnInit {
  dialogRef = inject<MatDialogRef<RoleDialogComponent>>(MatDialogRef);
  dialog = inject(MatDialog);
  private snacky = inject(MatSnackBar);
  data = inject(MAT_DIALOG_DATA);

  form: FormGroup;
  type = 'model';
  headline: string;
  content: string;
  confirm: string;
  placeholder: string;
  update = false;
  required = Validators.required;
  separatorKeysCodes: number[] = [ENTER, COMMA];
  loading = true;
  rightCtrl = new FormControl();
  @ViewChild('rightsInput', { static: false })
  rightsInput: ElementRef<HTMLInputElement>;
  add = [];
  drop = [];
  ogRights = [];
  allRights = [];
  filteredRights: Observable<any[]>;

  constructor() {
    this.filteredRights = this.rightCtrl.valueChanges.pipe(
      startWith(null),
      map((right: string | null) =>
        right
          ? this._filterRights(right, this.allRights)
          : this.allRights.slice()
      )
    );
  }

  ngOnInit() {
    let roleData = {};

    if (this.data) {
      this.headline = 'Update Role';
      roleData = this.data.role;
      if (!this.data.addRole) {
        this.update = true;
      }
    }
    const formGroup = {};
    formGroup['name'] = new FormControl(
      roleData ? roleData['name'] : '',
      this.required
    );
    formGroup['id'] = new FormControl(
      roleData ? roleData['id'] : '',
      this.required
    );
    formGroup['assignedRights'] = new FormControl(
      roleData ? roleData['rights'] : []
    );
    if (roleData) {
      this.ogRights = [...roleData['rights']];
    }
    this.allRights = this.data.rights;
    this.form = new UntypedFormGroup(formGroup);
    this.loading = false;
  }

  focusRights() {
    this.rightsInput.nativeElement.blur();
    this.rightsInput.nativeElement.focus();
  }

  addItem(
    event: MatChipInputEvent,
    items: Array<any>,
    control: FormControl
  ): void {
    const input = event.input;
    const value = event.value;

    const exisitng = items.filter(x => x.ID === value['ID']);

    if ((value || '').trim() && exisitng.length < 1) {
      items.push(value.trim());
    }

    if (input) {
      input.value = '';
    }

    control.setValue(null);
  }

  cancel() {
    this.dialogRef.close({ result: 'cancel' });
  }

  delete() {
    let confirmDialogRef = this.dialog.open(WarnDialogComponent, {
      panelClass: 'warn-dialog',
      data: {
        headline: 'Delete Role?',
        content: 'Please confirm role deletion.',
        confirm: 'Delete',
      },
    });
    confirmDialogRef.afterClosed().subscribe(result => {
      if (result === 'continue') {
        this.dialogRef.close({
          result: 'delete',
          data: this.form.value,
          add: [],
          drop: [],
        });
      }
      confirmDialogRef = null;
    });
  }

  save() {
    if (this.form.valid) {
      this.dialogRef.close({
        result: this.update ? 'update' : 'add',
        data: this.form.value,
        add: this.add,
        drop: this.drop,
      });
      this.dialogRef.afterClosed().subscribe(() => {
        this.dialogRef = null;
      });
    }
  }

  // Only AlphaNumeric with Some Characters [-_ ]
  alphaNumericWithCharacters(event) {
    const inp = String.fromCharCode(event.keyCode);
    // Allow numbers, alpahbets, space, underscore
    if (/[a-zA-Z0-9-_ ]/.test(inp)) {
      return true;
    } else {
      event.preventDefault();
      return false;
    }
  }

  selectedRight(event: MatAutocompleteSelectedEvent): void {
    const addRight = this.data.rights.find(x => x.id === event.option.value);

    if (!this.data.role.rights.includes(addRight.id)) {
      this.data.role.rights.push(addRight.id);
      this.rightsInput.nativeElement.value = '';
      this.rightCtrl.setValue(null);

      if (!this.ogRights.includes(addRight.id)) {
        this.add = [...this.add, addRight.id];
      }

      if (this.drop.includes(addRight.id)) {
        this.drop = this.drop.splice(
          this.drop.findIndex(dropRight => dropRight === addRight.id),
          1
        );
      }
    } else {
      this.snacky.open(
        `${addRight.name} is already assigned to this role!`,
        'Close',
        {
          duration: 3000,
        }
      );
    }
  }

  removeAssignedRight(right) {
    if (this.data.role.rights.includes(right)) {
      this.data.role.rights = this.data.role.rights.filter(x => x !== right);

      if (this.add.includes(right)) {
        this.add = this.add.filter(addRight => addRight !== right);
      }

      if (this.ogRights.includes(right)) {
        this.drop = [...this.drop, right];
      }
    }
  }

  private _filterRights(value: string, items: Array<any>): string[] {
    const filterValue = value.toLowerCase();

    return items.filter(
      unit => unit.id.toLowerCase().indexOf(filterValue) === 0
    );
  }

  selectedRightDisplay(rightId, idDisplay = false) {
    const returnRight = this.data.rights.find(right => right.id === rightId);
    if (returnRight && !idDisplay) {
      return this.data.rights.find(right => right.id === rightId).name;
    } else if (returnRight && idDisplay) {
      return this.data.rights.find(right => right.id === rightId).id;
    }
    // else if (!returnRight && !idDisplay) {
    //   return 'Right not found!';
    // }
    else if (!returnRight && idDisplay) {
      return rightId;
    }
    return rightId;
  }
}
